/**
 * Desktop zoom dialog: full-viewport image (contain) + pointer drag + wheel zoom.
 * Wheel is handled on the dialog (capture) so it works over letterbox / dark areas.
 */
(function () {
  const DESKTOP = window.matchMedia('(min-width: 750px)');
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const DRAG_THRESHOLD = 6;

  /** @type {WeakMap<Element, { scale: number, tx: number, ty: number, dragging: boolean, startX: number, startY: number, startTx: number, startTy: number, moved: boolean }>} */
  const stateMap = new WeakMap();

  function enabled() {
    return DESKTOP.matches;
  }

  function getState(wrapper) {
    let s = stateMap.get(wrapper);
    if (!s) {
      s = {
        scale: MIN_SCALE,
        tx: 0,
        ty: 0,
        dragging: false,
        startX: 0,
        startY: 0,
        startTx: 0,
        startTy: 0,
        moved: false,
      };
      stateMap.set(wrapper, s);
    }
    return s;
  }

  function applyTransform(wrapper, s) {
    wrapper.style.setProperty('--drag-zoom-scale', String(s.scale));
    wrapper.style.setProperty('--drag-zoom-translate-x', `${s.tx}px`);
    wrapper.style.setProperty('--drag-zoom-translate-y', `${s.ty}px`);
    const dialog = wrapper.closest('dialog');
    if (dialog) {
      dialog.classList.toggle('uucck-pdp-zoom-scroll-lock', s.scale > MIN_SCALE);
    }
  }

  function resetWrapper(wrapper) {
    const s = getState(wrapper);
    s.scale = MIN_SCALE;
    s.tx = 0;
    s.ty = 0;
    s.dragging = false;
    s.moved = false;
    wrapper.classList.remove('is-dragging');
    applyTransform(wrapper, s);
  }

  function constrain(wrapper, s) {
    const img = wrapper.querySelector('.product-media__image');
    if (!img) return;

    s.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s.scale));

    if (s.scale <= MIN_SCALE) {
      s.tx = 0;
      s.ty = 0;
      applyTransform(wrapper, s);
      return;
    }

    const rect = wrapper.getBoundingClientRect();
    let nw = img.naturalWidth;
    let nh = img.naturalHeight;
    if (!nw || !nh) {
      nw = rect.width;
      nh = rect.height;
    }

    const imageAR = nw / nh;
    const wrapAR = rect.width / rect.height;
    let fitW;
    let fitH;
    if (imageAR > wrapAR) {
      fitW = rect.width;
      fitH = fitW / imageAR;
    } else {
      fitH = rect.height;
      fitW = fitH * imageAR;
    }

    const scaledW = fitW * s.scale;
    const scaledH = fitH * s.scale;
    const maxTx = Math.max(0, (scaledW - rect.width) / 2) / s.scale;
    const maxTy = Math.max(0, (scaledH - rect.height) / 2) / s.scale;

    s.tx = Math.min(maxTx, Math.max(-maxTx, s.tx));
    s.ty = Math.min(maxTy, Math.max(-maxTy, s.ty));
    applyTransform(wrapper, s);
  }

  function applyWheelZoom(wrapper, deltaY) {
    const s = getState(wrapper);
    const step = Math.min(0.35, Math.max(0.06, Math.abs(deltaY) * 0.002));
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s.scale + (deltaY > 0 ? -step : step)));
    if (Math.abs(next - s.scale) < 0.001) return false;
    s.scale = next;
    if (s.scale <= MIN_SCALE) {
      s.tx = 0;
      s.ty = 0;
    }
    constrain(wrapper, s);
    return true;
  }

  function getActiveWrapper(dialog) {
    const items = [...dialog.querySelectorAll('.dialog-zoomed-gallery > .product-media-container--image')];
    if (!items.length) return null;

    let best = items[0];
    let bestVisible = 0;
    const vh = window.innerHeight;

    for (const el of items) {
      const r = el.getBoundingClientRect();
      const visible = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
      if (visible > bestVisible) {
        bestVisible = visible;
        best = el;
      }
    }

    return best.querySelector('drag-zoom-wrapper');
  }

  function syncDialogOpen(dialog) {
    if (!enabled() || !dialog.open) return;
    dialog.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => {
      bindWrapper(wrapper);
      resetWrapper(wrapper);
    });
  }

  function bindWrapper(wrapper) {
    if (wrapper.dataset.uucckDialogZoom === '1') return;
    wrapper.dataset.uucckDialogZoom = '1';

    wrapper.addEventListener(
      'click',
      (event) => {
        const s = getState(wrapper);
        if (s.moved) {
          event.preventDefault();
          event.stopPropagation();
          s.moved = false;
        }
      },
      true
    );

    wrapper.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0) return;
        const s = getState(wrapper);
        s.dragging = true;
        s.moved = false;
        s.startX = event.clientX;
        s.startY = event.clientY;
        s.startTx = s.tx;
        s.startTy = s.ty;
        wrapper.classList.add('is-dragging');
        wrapper.setPointerCapture(event.pointerId);
      },
      { passive: true }
    );

    wrapper.addEventListener(
      'pointermove',
      (event) => {
        const s = getState(wrapper);
        if (!s.dragging || s.scale <= MIN_SCALE) return;

        const dx = event.clientX - s.startX;
        const dy = event.clientY - s.startY;
        if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

        s.moved = true;
        event.preventDefault();
        s.tx = s.startTx + dx / s.scale;
        s.ty = s.startTy + dy / s.scale;
        constrain(wrapper, s);
      },
      { passive: false }
    );

    const endDrag = (event) => {
      const s = getState(wrapper);
      if (!s.dragging) return;
      s.dragging = false;
      wrapper.classList.remove('is-dragging');
      try {
        wrapper.releasePointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
      constrain(wrapper, s);
    };

    wrapper.addEventListener('pointerup', endDrag);
    wrapper.addEventListener('pointercancel', endDrag);

    resetWrapper(wrapper);
  }

  function bindDialog(dialog) {
    if (dialog.dataset.uucckDialogZoomHook === '1') return;
    dialog.dataset.uucckDialogZoomHook = '1';

    dialog.querySelectorAll('drag-zoom-wrapper').forEach(bindWrapper);

    dialog.addEventListener('toggle', () => syncDialogOpen(dialog));

    dialog.addEventListener(
      'close',
      () => {
        dialog.querySelectorAll('drag-zoom-wrapper').forEach(resetWrapper);
      },
      true
    );

    dialog.addEventListener(
      'wheel',
      (event) => {
        if (!enabled() || !dialog.open) return;
        if (event.target instanceof Element && event.target.closest('.dialog-thumbnails-list-container')) {
          return;
        }

        const wrapper = getActiveWrapper(dialog);
        if (!wrapper) return;

        const s = getState(wrapper);
        const zoomed = applyWheelZoom(wrapper, event.deltaY);

        if (zoomed || s.scale > MIN_SCALE) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { passive: false, capture: true }
    );
  }

  function boot() {
    if (!enabled()) return;
    document.querySelectorAll('zoom-dialog dialog.dialog-zoomed-gallery__dialog').forEach(bindDialog);
  }

  DESKTOP.addEventListener('change', boot);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  new MutationObserver(() => boot()).observe(document.documentElement, { childList: true, subtree: true });
})();
